import React, { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Grid,
  Typography,
  Breadcrumbs,
  Divider,
  Card,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { Save, Visibility, InfoOutlined } from '@mui/icons-material';
import { IconButton } from '@mui/material';

import { useResponseHandler } from '../../../hooks/useResponseHandler';
import {
  mapKnowledgeBaseToBackend,
  mapKnowledgeBaseToFrontend,
} from './mapper/baseConocimiento.mapper';
import InputField from '../../../components/formFields/InputField';
import AutocompleteField from '../../../components/formFields/autocompleteField';
import { UploadButtonField } from '../../../components/formFields/uploadButtonField';
import { getKnowledgeBaseById, updateKnowledgeBase } from 'store/knowledge-bases/thunks';
import { SnackbarProgress } from '../../../components';
import { ModalViewPdfGlobal } from '../../../components/modals/viwerPdf/ModalViewPdfGlobal';
import { InfoModal } from '../../../components/generics/InfoModal';
import { baseConocimientoFormModel, formInitialValues, editValidationSchema } from './formik';

const StyledCard = styled(Card)(({ theme }) => ({
  marginTop: 50,
  padding: 24,
  borderRadius: 16,
  boxShadow:
    '0px 12px 24px -4px rgba(145, 158, 171, 0.12), 0px 0px 2px 0px rgba(145, 158, 171, 0.2)',
  border: '1px solid rgba(145, 158, 171, 0.16)',
}));

const { formField } = baseConocimientoFormModel;

export const EditBaseConocimiento = () => {
  const { id } = useParams();
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { handleResponse, ResponseComponents } = useResponseHandler() as any;

  const [loading, setLoading] = useState(true);
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [activeModal, setActiveModal] = useState<string | null>(null);

  // Estado global del modal PDF (igual que en DetailEmployee)
  const [showPdfDetail, setShowPdfDetail] = useState(false);
  const [urlRutPdf, setUrlRutPdf] = useState<string | null>(null);
  const [typeUrl, setTypeUrl] = useState<string | null>(null);

  const handleOpenPdf = (url: string, urlTypeUrl: string) => {
    setUrlRutPdf(url);
    setTypeUrl(urlTypeUrl);
    setShowPdfDetail(true);
  };

  const [initialData, setInitialData] = useState<any>(formInitialValues);

  // Guardamos las opciones dinámicas que vienen ahora integradas en la data del backend
  const [providers, setProviders] = useState<any[]>([]);
  const [embeddingModels, setEmbeddingModels] = useState<any[]>([]);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    // Ya no hace falta dispatch(getDataFormDropDownStore())
    const response = await dispatch(getKnowledgeBaseById(id as string));
    const success = await handleResponse(response, 'cargar base de conocimiento', false);

    if (success && response.data) {
      const data = response.data;

      // Asignamos las opciones del backend (que modificaste)
      const loadedProviders = data?.formToDropdown?.providers || [];
      const loadedModels = data?.formToDropdown?.services || [];
      setProviders(loadedProviders);
      setEmbeddingModels(loadedModels);

      // Mapeamos los datos para el formulario
      const mapped = mapKnowledgeBaseToFrontend(data);
      if (mapped.embeddingProvider && typeof mapped.embeddingProvider === 'string') {
        const foundProvider = loadedProviders.find(
          (p: any) => p.value === mapped.embeddingProvider
        );
        if (foundProvider) mapped.embeddingProvider = foundProvider;
      }

      setInitialData(mapped);
    } else {
      navigate('/inteligencia-artificial/base-conocimiento');
    }
    setLoading(false);
  };

  const handleSubmit = async (values: any, actions: any) => {
    const formData = new FormData();
    formData.append('name', values.name);
    if (values.description) formData.append('description', values.description);

    const providerValue =
      values.embeddingProvider && typeof values.embeddingProvider === 'object'
        ? values.embeddingProvider.value
        : values.embeddingProvider;
    if (providerValue) formData.append('embeddingProvider', providerValue);

    const modelValue =
      values.embeddingModel && typeof values.embeddingModel === 'object'
        ? values.embeddingModel.value
        : values.embeddingModel;
    if (modelValue) formData.append('embeddingModel', modelValue);

    if (values.file && values.file instanceof File) {
      formData.append('file', values.file);
    }

    const response = await dispatch(
      updateKnowledgeBase(id as string, formData, setUploadPercentage)
    );
    const success = await handleResponse(response, 'actualizar base de conocimiento', true);

    setTimeout(() => setUploadPercentage(0), 1000);

    actions.setSubmitting(false);
  };

  const getModalContent = () => {
    switch (activeModal) {
      case 'name':
        return {
          title: 'Nombre',
          description: 'El nombre identificador de esta base de conocimiento.',
          infoText: 'Ejemplo: "Reglamento del Hotel" o "Políticas de Cancelación".',
        };
      case 'description':
        return {
          title: 'Descripción',
          description:
            'Una breve explicación del contenido o propósito de esta base de conocimiento.',
          infoText: 'Ejemplo: "Contiene las normas internas y horarios para los empleados."',
        };
      case 'embeddingProvider':
        return {
          title: 'Proveedor de Embedding',
          description:
            'La empresa que provee el modelo matemático que convertirá tus textos en vectores.',
          infoText:
            'Ejemplo: Google (Gemini) u OpenAI. Esto define qué infraestructura procesará tus documentos.',
        };
      case 'embeddingModel':
        return {
          title: 'Modelo de Embedding',
          description:
            'El modelo de inteligencia artificial específico utilizado para procesar los textos.',
          infoText:
            'Ejemplo: gemini-1.5-pro o text-embedding-3-small. Diferentes modelos tienen diferentes niveles de comprensión y costos.',
        };
      default:
        return { title: '', description: '', infoText: '' };
    }
  };

  const modalContent = getModalContent();

  const renderFieldWithInfo = (field: React.ReactNode, modalKey: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flexGrow: 1 }}>{field}</Box>
      <IconButton color="info" onClick={() => setActiveModal(modalKey)}>
        <InfoOutlined />
      </IconButton>
    </Box>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {ResponseComponents}

      <SnackbarProgress
        open={uploadPercentage > 0 && uploadPercentage < 100}
        percentage={uploadPercentage}
        label="Actualizando base de conocimiento..."
      />

      <Typography fontSize={25} color="textPrimary" sx={{ fontWeight: 700 }}>
        Editar Base de Conocimiento
      </Typography>
      <Breadcrumbs aria-label="breadcrumb" sx={{ padding: 2 }}>
        <Typography color="textPrimary" sx={{ fontWeight: 700 }}>
          IA
        </Typography>
        <Link
          style={{ color: 'inherit', textDecoration: 'none' }}
          to="/inteligencia-artificial/base-conocimiento"
        >
          Bases de Conocimiento
        </Link>
        <Typography color="textPrimary">Editar</Typography>
      </Breadcrumbs>
      <Divider sx={{ marginBottom: 3 }} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Formik
          initialValues={initialData}
          validationSchema={editValidationSchema}
          enableReinitialize={true}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, values, setFieldValue }) => {
            const currentProviderValue =
              values.embeddingProvider?.value || values.embeddingProvider;
            const currentProvider =
              providers.find((p: any) => p.value === currentProviderValue) || null;

            const filteredModels = currentProvider
              ? embeddingModels
                  .filter((m: any) => m.provider === currentProvider.value)
                  .map((m: any) => ({ value: m.code, label: m.name }))
              : [];

            // Efecto para ajustar el modelo si viene como string
            if (
              values.embeddingModel &&
              typeof values.embeddingModel === 'string' &&
              filteredModels.length > 0
            ) {
              const foundModel = filteredModels.find((m: any) => m.value === values.embeddingModel);
              if (foundModel) {
                setTimeout(() => setFieldValue(formField.embeddingModel.name, foundModel), 0);
              }
            }

            return (
              <StyledCard>
                <Form autoComplete="nope" id={baseConocimientoFormModel.formId}>
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" color="primary" sx={{ mb: 3 }}>
                      Información General
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        {renderFieldWithInfo(
                          <InputField
                            name={formField.name.name}
                            label={formField.name.label}
                            fullWidth
                            variant="outlined"
                          />,
                          'name'
                        )}
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        {renderFieldWithInfo(
                          <InputField
                            name={formField.description.name}
                            label={formField.description.label}
                            fullWidth
                            variant="outlined"
                          />,
                          'description'
                        )}
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        {renderFieldWithInfo(
                          <AutocompleteField
                            name={formField.embeddingProvider.name}
                            label={formField.embeddingProvider.label}
                            options={providers}
                            onChange={(event: any, newValue: any) => {
                              setFieldValue(formField.embeddingModel.name, null);
                            }}
                            fullWidth
                          />,
                          'embeddingProvider'
                        )}
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        {renderFieldWithInfo(
                          <AutocompleteField
                            name={formField.embeddingModel.name}
                            label={formField.embeddingModel.label}
                            options={filteredModels}
                            disabled={!values.embeddingProvider}
                            fullWidth
                          />,
                          'embeddingModel'
                        )}
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          {formField.file.label}
                        </Typography>
                        <UploadButtonField
                          name={formField.file.name}
                          text="Actualizar Archivo (PDF)"
                          accept=".pdf"
                          onChange={(e: any) => {
                            if (e.target.files && e.target.files.length > 0) {
                              setFieldValue('file', e.target.files[0]);
                            }
                          }}
                        />
                        {values.file && (values.file as any).name ? (
                          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="textSecondary">
                              Nuevo archivo seleccionado: {(values.file as any).name}
                            </Typography>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                const fileUrl = URL.createObjectURL(values.file as any);
                                handleOpenPdf(fileUrl, 'local');
                              }}
                              title="Ver documento"
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          initialData?.originalName && (
                            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" color="textSecondary">
                                Archivo actual: {initialData.originalName}
                              </Typography>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() =>
                                  handleOpenPdf(initialData.fileName, 'knowledge-bases')
                                }
                                title="Ver documento"
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Box>
                          )
                        )}
                      </Grid>
                    </Grid>
                  </Box>

                  <ModalViewPdfGlobal
                    open={showPdfDetail}
                    typeUrl={typeUrl}
                    onClose={() => setShowPdfDetail(false)}
                    pdfUrl={urlRutPdf}
                  />

                  <Divider sx={{ my: 4 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/inteligencia-artificial/base-conocimiento')}
                      disabled={isSubmitting}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="contained"
                      type="submit"
                      color="primary"
                      startIcon={<Save />}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
                    </Button>
                  </Box>
                </Form>
              </StyledCard>
            );
          }}
        </Formik>
      )}

      <InfoModal
        open={!!activeModal}
        setOpen={(isOpen: boolean) => {
          if (!isOpen) setActiveModal(null);
        }}
        title={modalContent.title}
        description={modalContent.description}
        infoText={modalContent.infoText}
        icon="Info"
      />
    </Box>
  );
};
